import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, buildImageUrl } from "../lib/api";
import {
  Pencil,
  Trash2,
  Plus,
  EyeOff,
  Eye,
  LogOut,
  Upload,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Сытные",
  "Сладкие",
  "Пирожки печеные",
  "Пирожки жареные",
  "Фаст фуд",
];

const EMPTY_FORM = {
  name: "",
  category: "Сытные",
  description: "",
  price: 0,
  weight_type: "variable",
  weight: "",
  image_url: "",
  is_active: true,
};

const PAGE_SIZE = 10;

const Pagination = ({ page, total, onPage }) => {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E3DA]">
      <span className="text-xs text-[#5C4F45]">
        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} из {total}
      </span>
      <div className="flex gap-1">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 0}
          className="w-8 h-8 rounded-full border border-[#E8E3DA] flex items-center justify-center disabled:opacity-30 hover:border-[#C65D3B] transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          onClick={() => onPage(page + 1)}
          disabled={page >= pages - 1}
          className="w-8 h-8 rounded-full border border-[#E8E3DA] flex items-center justify-center disabled:opacity-30 hover:border-[#C65D3B] transition-colors"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productPage, setProductPage] = useState(0);
  const [spamProtection, setSpamProtection] = useState(
    () => localStorage.getItem("pirogi_spam_protection") !== "false"
  );
  const fileRef = useRef(null);

  const toggleSpam = () => {
    const next = !spamProtection;
    setSpamProtection(next);
    localStorage.setItem("pirogi_spam_protection", String(next));
    toast.success(next ? "Защита от спама включена" : "Защита от спама отключена");
  };

  const reload = async () => {
    const p = await api.get("/products");
    setProducts(p.data);
  };

  useEffect(() => {
    if (admin) reload();
  }, [admin]);

  const pagedProducts = useMemo(
    () => products.slice(productPage * PAGE_SIZE, (productPage + 1) * PAGE_SIZE),
    [products, productPage],
  );

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name,
      category: p.category,
      description: p.description,
      price: p.price,
      weight_type: p.weight_type,
      weight: p.weight || "",
      image_url: p.image_url,
      is_active: p.is_active,
    });
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name || !form.image_url || !form.category) {
      toast.error("Заполните название, категорию и фото");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        weight: form.weight_type === "fixed" ? form.weight : null,
      };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Товар добавлен");
      } else {
        await api.put(`/products/${editing}`, payload);
        toast.success("Товар обновлён");
      }
      close();
      reload();
    } catch (err) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Удалить товар?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Товар удалён");
    reload();
  };

  const handleToggleActive = async (p) => {
    await api.put(`/products/${p.id}`, { is_active: !p.is_active });
    reload();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const { data } = await api.post("/upload", { data: dataUrl });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Фото загружено");
    } catch (err) {
      toast.error("Ошибка загрузки");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="label-eyebrow mb-2">Админ-панель</div>
          <h1 className="font-display text-4xl text-[#2C2520]">
            Здравствуйте, {admin.email}
          </h1>
        </div>
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="admin-logout"
        >
          <LogOut size={16} /> Выйти
        </button>
      </div>


      <div className="flex items-center justify-between bg-white border border-[#E8E3DA] rounded-2xl px-5 py-3 mb-6">
        <div>
          <div className="text-sm font-medium text-[#2C2520]">Защита от спама</div>
          <div className="text-xs text-[#5C4F45]">1 заявка в час с одного устройства</div>
        </div>
        <button
          onClick={toggleSpam}
          style={{ background: spamProtection ? "#2C2520" : "#E8E3DA" }}
          className="relative w-12 h-6 rounded-full transition-colors flex-shrink-0"
        >
          <span
            style={{ transform: spamProtection ? "translateX(28px)" : "translateX(4px)" }}
            className="absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform"
          />
        </button>
      </div>

      <div data-testid="products-tab">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl text-[#2C2520]">Каталог</h2>
            <button onClick={openNew} className="btn-primary" data-testid="add-product-button">
              <Plus size={16} /> Добавить
            </button>
          </div>

          <div className="bg-white border border-[#E8E3DA] rounded-[24px] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#F2EBE1] text-[#5C4F45]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Фото</th>
                    <th className="text-left px-4 py-3 font-medium">Название</th>
                    <th className="text-left px-4 py-3 font-medium">Категория</th>
                    <th className="text-left px-4 py-3 font-medium">Цена</th>
                    <th className="text-left px-4 py-3 font-medium">Вес</th>
                    <th className="text-left px-4 py-3 font-medium">Статус</th>
                    <th className="text-right px-4 py-3 font-medium">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedProducts.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-[#E8E3DA]"
                      data-testid={`admin-product-row-${p.id}`}
                    >
                      <td className="px-4 py-3">
                        <img
                          src={buildImageUrl(p.image_url)}
                          alt={p.name}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                      </td>
                      <td className="px-4 py-3 text-[#2C2520] font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-[#5C4F45]">{p.category}</td>
                      <td className="px-4 py-3 text-[#2C2520]">{p.price} ₽</td>
                      <td className="px-4 py-3 text-[#5C4F45]">
                        {p.weight_type === "fixed" ? p.weight : "1/2 кг"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${
                            p.is_active
                              ? "bg-[#E5EBE6] text-[#4A5D4E]"
                              : "bg-[#F2EBE1] text-[#5C4F45]"
                          }`}
                        >
                          {p.is_active ? "Активен" : "Скрыт"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleActive(p)}
                            className="w-9 h-9 rounded-full border border-[#E8E3DA] hover:border-[#C65D3B] flex items-center justify-center"
                            title={p.is_active ? "Скрыть" : "Показать"}
                            data-testid={`toggle-${p.id}`}
                          >
                            {p.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button
                            onClick={() => openEdit(p)}
                            className="w-9 h-9 rounded-full border border-[#E8E3DA] hover:border-[#C65D3B] flex items-center justify-center"
                            data-testid={`edit-${p.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            className="w-9 h-9 rounded-full border border-[#E8E3DA] hover:border-[#9E3C3C] hover:text-[#9E3C3C] flex items-center justify-center"
                            data-testid={`delete-${p.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={productPage} total={products.length} onPage={setProductPage} />
          </div>
        </div>

      {editing !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#2C2520]/60 flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="bg-white rounded-[28px] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="product-editor"
          >
            <div className="flex items-center justify-between px-7 py-5 border-b border-[#E8E3DA]">
              <h3 className="font-display text-2xl text-[#2C2520]">
                {editing === "new" ? "Новый товар" : "Редактирование"}
              </h3>
              <button
                onClick={close}
                className="w-9 h-9 rounded-full border border-[#E8E3DA] hover:border-[#C65D3B] flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-7 space-y-5">
              <div>
                <label className="label-eyebrow block mb-2">Фото</label>
                <div className="flex items-center gap-4">
                  {form.image_url && (
                    <img
                      src={buildImageUrl(form.image_url)}
                      alt=""
                      className="w-24 h-24 rounded-2xl object-cover bg-[#F2EBE1]"
                    />
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFile}
                      className="hidden"
                      data-testid="image-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="btn-ghost inline-flex items-center gap-2"
                      data-testid="upload-image-button"
                    >
                      {uploading ? (
                        <><Loader2 size={14} className="animate-spin" /> Загрузка...</>
                      ) : (
                        <><Upload size={14} /> Загрузить фото</>
                      )}
                    </button>
                    <input
                      type="text"
                      placeholder="или вставьте URL картинки"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="w-full px-3 py-2 text-xs rounded-xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                      data-testid="image-url-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-2">Название</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Категория</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                    data-testid="form-category"
                  >
                    {CATEGORIES.map((c) => (<option key={c}>{c}</option>))}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Цена, ₽</label>
                  <input
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                    data-testid="form-price"
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Тип веса</label>
                  <select
                    value={form.weight_type}
                    onChange={(e) => setForm({ ...form, weight_type: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                    data-testid="form-weight-type"
                  >
                    <option value="variable">Развесной (1кг / 2кг)</option>
                    <option value="fixed">Штучный</option>
                  </select>
                </div>
                {form.weight_type === "fixed" && (
                  <div className="sm:col-span-2">
                    <label className="label-eyebrow block mb-2">Вес штучный (напр. 70г)</label>
                    <input
                      type="text"
                      value={form.weight}
                      onChange={(e) => setForm({ ...form, weight: e.target.value })}
                      className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B]"
                      data-testid="form-weight"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="label-eyebrow block mb-2">Описание</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border border-[#E8E3DA] bg-white focus:outline-none focus:border-[#C65D3B] resize-none"
                  data-testid="form-description"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="accent-[#C65D3B] w-4 h-4"
                  data-testid="form-active"
                />
                <span className="text-sm text-[#2C2520]">Активен (виден покупателям)</span>
              </label>
            </div>

            <div className="px-7 py-5 border-t border-[#E8E3DA] flex justify-end gap-3">
              <button onClick={close} className="btn-ghost">Отмена</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
                data-testid="save-product-button"
              >
                {saving ? (
                  <><Loader2 size={14} className="animate-spin" /> Сохранение...</>
                ) : (
                  "Сохранить"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
